import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@clarix/api-client';
import type { ListWorkspaceMastersResponse } from '@clarix/shared-types';

/**
 * Cross-app change signal — the workspace remote fires this window CustomEvent
 * after it creates / edits / deletes / imports a workspace master
 * (apps/workspace/src/api/workspace-admin.api.ts). The workspace app mutates
 * through RTK Query while this hook reads through React Query in the SHELL's
 * React tree, so its `invalidatesTags: ['WorkspaceMaster']` cannot reach this
 * cache and the sidebar counts would sit stale for `staleTime`.
 * KEEP THE NAME IN SYNC with that emitter.
 */
export const WORKSPACE_MASTERS_CHANGED_EVENT = 'workspace:masters-changed';

/** Announce that workspace masters changed, so every reader refetches. */
export function emitWorkspaceMastersChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(WORKSPACE_MASTERS_CHANGED_EVENT));
}

/**
 * Read-only view of GET /workspaces/:slug/admin/masters — workspace taxonomies
 * (designations, departments, …) plus their category descriptors. Lets the
 * shell's sidebar panel list categories without importing admin app source.
 * Mutations live in the workspace remote (apps/workspace/src/api/workspace-admin.api.ts)
 * on a SEPARATE (RTK Query) cache, so this hook refetches on the change event
 * above rather than on any shared query key.
 */
export function useWorkspaceMasters(slug: string | null) {
  const query = useQuery({
    queryKey: ['ws', slug, 'masters'] as const,
    queryFn: () => apiService.get<ListWorkspaceMastersResponse>(`/workspaces/${slug}/admin/masters`),
    enabled: !!slug,
    staleTime: 30_000,
  });

  const { refetch } = query;
  useEffect(() => {
    const onChanged = () => void refetch();
    window.addEventListener(WORKSPACE_MASTERS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(WORKSPACE_MASTERS_CHANGED_EVENT, onChanged);
  }, [refetch]);

  return query;
}
